'use strict';

define('vb/private/stateManagement/routerUtils',['vb/private/constants'], (Constants) => {
  /**
   * @typedef {import('vb/private/stateManagement/page.js').default} Page
   * @typedef {import('vb/private/stateManagement/router/vbRouter.js').default} VbRouter
   */

  /**
   * Traverse the router hierarchy to retrieve the current page.
   * The content of the application is made of nested flows and the leaf flow is
   * displaying a page. This routine traverse the hierarchy of flows using the
   * router hierarchy and return the page on the leaf flow.
   *
   * When the full argument is truthy, the function retrieve the current page of
   * the switcher.
   *
   * @param  {VbRouter} rt router
   * @param  {boolean} [full=false] when true, traverse the full container hierarchy to the switcher current page
   * @return {Page | null}   the current page
   */
  function getPageInternal(rt, full = false) {
    if (!rt) {
      return null;
    }

    const currentState = rt.currentState();
    if (!currentState) {
      return null;
    }

    // The container is set in the router configure callback
    const container = currentState.value;
    if (!container || !container.router) {
      return null;
    }

    const { id } = currentState;
    const childRouter = container.router.getChildRouter(encodeURIComponent(id));
    let page = getPageInternal(childRouter, full);
    if (!page) {
      page = container.getContainer(id);
      if (full && page && page.switcherBridge) {
        // Drill into the switcher element
        const switcherElement = page.switcherBridge.getCurrentSwitcherElement();
        // When all the element are removed from the switcher, getCurrentSwitcherElement returns null and
        // we should return the page of the switcher root
        if (!switcherElement) {
          return page;
        }
        return switcherElement.getCurrentPage();
      }
    }

    return page;
  }

  /**
   * This class describes router utilities.
   *
   */
  class RouterUtils {
    /**
     * Traverse the router hierarchy to retrieve the current page.
     * The content of the application is made of nested flows and the leaf flow is
     * displaying a page. This routine traverse the hierarchy of flows using the
     * router hierarchy and return the page on the leaf flow.
     *
     * When the full argument is truthy, the function retrieve the current page of
     * the switcher.
     *
     * @param  {VbRouter} router
     * @param  {boolean} [full=false] when true, traverse the full container hierarchy to the switcher current page
     * @return {Page}   the current page
     */
    static getCurrentPage(router, full = false) {
      return getPageInternal(router, full);
    }

    /**
     * Build the query parameters representing the current page when using the
     * query router strategy.
     *
     * @param {VbRouter} rt the base router
     * @param {Array<Object>} params array of non default parameter
     * @param {Array<Object>} finalParams complete array of parameters for the URL
     * @return {Array<Object>} finalParams complete array of parameters for the URL
     */
    static buildSearch(rt, params = [], finalParams = []) {
      const currentState = rt.currentState();
      if (currentState) {
        const name = rt.name;
        const id = currentState.id;

        params.push({ name, id });

        // Default state is not shown on the URL
        if (id !== rt.defaultStateId) {
          finalParams = finalParams.concat(params); // eslint-disable-line no-param-reassign
          params = []; // eslint-disable-line no-param-reassign
        }

        // The container is set in the router configure callback
        const container = currentState.value;
        const childRouter = container.router.getChildRouter(id);
        if (childRouter) {
          return RouterUtils.buildSearch(childRouter, params, finalParams);
        }
      }

      return finalParams;
    }

    /**
     * Following code restores special query params if they exist in the session storage
     * Called from main.js and tests
     * This is for preview mode and FA test
     */
    static specialSearchParamsHandling() {
      const { sessionStorage } = globalThis;
      if (sessionStorage) {
        let change = false;
        const url = new URL(window.location.href);
        const { searchParams } = url;
        // For vbdt:preferExtensionVersion and vbdtRedwoodOverride params
        Constants.UrlParamsToRestore.forEach((param) => {
          let value = searchParams.get(param);
          if (value) {
            // Store the param on the session so it can be retrieved on refresh
            sessionStorage.setItem(param, value);
          } else {
            // If the param is not on the URL but in the session, add it to the URL
            value = sessionStorage.getItem(param);
            if (value) {
              searchParams.set(param, value);
              change = true;
            }
          }
        });

        if (change) {
          window.history.replaceState(window.history.state, '', url.href);
        }
      }
    }
  }

  return RouterUtils;
});

